import React, { useState, useEffect } from "react";
import { useEducation } from "../../../hooks/useEducation";
import {
  Close,
  CheckCircle,
  RadioButtonUnchecked,
  RadioButtonChecked,
  CheckBox,
  CheckBoxOutlineBlank,
  Timer,
  Send,
} from "@mui/icons-material";
import "./QuizModal.css";

const QuizModal = ({
  isOpen,
  onClose,
  quizData,
  contentId,
  classProgress,
  onQuizCompleted,
}) => {
  const [questions, setQuestions] = useState([]);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const { submitQuizResult } = useEducation();

  // Initialize quiz data
  useEffect(() => {
    if (quizData && isOpen) {
      setLoading(true);
      try {
        setQuestions(quizData.questions || []);

        // Set time limit if available
        if (quizData.timeLimit) {
          setTimeRemaining(quizData.timeLimit * 60); // Convert minutes to seconds
        }

        setStartTime(Date.now());
      } catch (error) {
        console.error("Error parsing quiz data:", error);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    }
  }, [quizData, isOpen]);

  // Shuffle questions for each student
  useEffect(() => {
    if (questions.length > 0) {
      const shuffled = shuffleQuestions(questions);
      setShuffledQuestions(shuffled);
    }
  }, [questions]);

  // Timer countdown
  useEffect(() => {
    if (
      timeRemaining === null ||
      timeRemaining <= 0 ||
      quizCompleted ||
      !isOpen
    ) {
      return;
    }

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - auto-submit
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining, quizCompleted, isOpen]);

  // Shuffle questions and their options
  const shuffleQuestions = (questionsArray) => {
    return questionsArray.map((question) => {
      const shuffledQuestion = { ...question };

      // Shuffle options if they exist
      if (question.options && Array.isArray(question.options)) {
        shuffledQuestion.options = shuffleArray([...question.options]);
      }

      return shuffledQuestion;
    });
  };

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Handle answer selection
  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  // Handle multiple choice selection
  const handleMultipleChoiceSelect = (questionId, selectedOption) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      let newAnswers;

      if (currentAnswers.includes(selectedOption)) {
        // Remove if already selected
        newAnswers = currentAnswers.filter(
          (option) => option !== selectedOption
        );
      } else {
        // Add if not selected
        newAnswers = [...currentAnswers, selectedOption];
      }

      return {
        ...prev,
        [questionId]: newAnswers,
      };
    });
  };

  // Navigate to next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  // Navigate to previous question
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  // Jump to specific question
  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      const timeTaken = startTime
        ? Math.floor((Date.now() - startTime) / 1000)
        : 0;

      // Submit to backend
      await submitQuizResult({
        content_id: contentId,
        answers: answers,
        time_taken: timeTaken,
      });

      // Calculate results
      const results = calculateResults();

      setQuizCompleted(true);
      setResults(results);

      if (onQuizCompleted) {
        onQuizCompleted(results);
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate quiz results
  const calculateResults = () => {
    let correctAnswers = 0;
    const questionResults = [];

    shuffledQuestions.forEach((question, index) => {
      const userAnswer = answers[question.id];
      const isCorrect = checkAnswer(question, userAnswer);

      if (isCorrect) {
        correctAnswers++;
      }

      questionResults.push({
        questionId: question.id,
        questionText: question.text,
        userAnswer: userAnswer,
        correctAnswer: question.correctAnswer,
        isCorrect: isCorrect,
        points: isCorrect ? question.points || 1 : 0,
      });
    });

    const totalQuestions = shuffledQuestions.length;
    const totalPoints = questionResults.reduce(
      (sum, result) => sum + result.points,
      0
    );
    const maxPoints = shuffledQuestions.reduce(
      (sum, question) => sum + (question.points || 1),
      0
    );
    const percentage =
      totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;

    return {
      totalQuestions,
      correctAnswers,
      totalPoints,
      maxPoints,
      percentage,
      questionResults,
      timeSpent: startTime ? Math.floor((Date.now() - startTime) / 1000) : null,
    };
  };

  // Check if answer is correct
  const checkAnswer = (question, userAnswer) => {
    if (!userAnswer) return false;

    const correctAnswer = question.correctAnswer;

    if (question.type === "multiple_choice") {
      // For multiple choice, user can select multiple options
      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        return (
          userAnswer.length === correctAnswer.length &&
          userAnswer.every((answer) => correctAnswer.includes(answer))
        );
      }
    } else {
      // For single choice
      return userAnswer === correctAnswer;
    }

    return false;
  };

  // Format time
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Handle modal close
  const handleClose = () => {
    if (!quizCompleted && shuffledQuestions.length > 0) {
      if (
        window.confirm(
          "Are you sure you want to close the quiz? Your progress will be lost."
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Render question
  const renderQuestion = (question) => {
    const userAnswer = answers[question.id];

    return (
      <div className="question-container">
        <div className="question-header">
          <h3 className="question-text">{question.text}</h3>
          {question.points && (
            <span className="question-points">{question.points} point(s)</span>
          )}
        </div>

        <div className="question-options">
          {question.options?.map((option, index) => {
            const isSelected =
              question.type === "multiple_choice"
                ? userAnswer?.includes(option)
                : userAnswer === option;

            return (
              <div
                key={index}
                className={`option-item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  if (question.type === "multiple_choice") {
                    handleMultipleChoiceSelect(question.id, option);
                  } else {
                    handleAnswerSelect(question.id, option);
                  }
                }}
              >
                <div className="option-icon">
                  {question.type === "multiple_choice" ? (
                    isSelected ? (
                      <CheckBox />
                    ) : (
                      <CheckBoxOutlineBlank />
                    )
                  ) : isSelected ? (
                    <RadioButtonChecked />
                  ) : (
                    <RadioButtonUnchecked />
                  )}
                </div>
                <span className="option-text">{option}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render results
  const renderResults = () => {
    if (!results) return null;

    return (
      <div className="quiz-results">
        <div className="results-header">
          <h2>Quiz Results</h2>
          <div className="results-summary">
            <div className="result-item">
              <span className="result-label">Score:</span>
              <span className="result-value">
                {results.correctAnswers}/{results.totalQuestions}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Percentage:</span>
              <span className="result-value">
                {results.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Points:</span>
              <span className="result-value">
                {results.totalPoints}/{results.maxPoints}
              </span>
            </div>
            {results.timeSpent && (
              <div className="result-item">
                <span className="result-label">Time:</span>
                <span className="result-value">
                  {formatTime(results.timeSpent)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="question-results">
          {results.questionResults.map((result, index) => (
            <div
              key={index}
              className={`question-result ${
                result.isCorrect ? "correct" : "incorrect"
              }`}
            >
              <div className="result-question">
                <span className="question-number">{index + 1}.</span>
                <span className="question-text">{result.questionText}</span>
                <span
                  className={`result-status ${
                    result.isCorrect ? "correct" : "incorrect"
                  }`}
                >
                  {result.isCorrect ? <CheckCircle /> : "✗"}
                </span>
              </div>
              <div className="result-answers">
                <div className="user-answer">
                  <strong>Your answer:</strong>{" "}
                  {Array.isArray(result.userAnswer)
                    ? result.userAnswer.join(", ")
                    : result.userAnswer || "Not answered"}
                </div>
                <div className="correct-answer">
                  <strong>Correct answer:</strong>{" "}
                  {Array.isArray(result.correctAnswer)
                    ? result.correctAnswer.join(", ")
                    : result.correctAnswer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="quiz-modal-overlay">
        <div className="quiz-modal">
          <div className="quiz-loading">
            <div className="loading-content">
              <div className="spinner"></div>
              Loading quiz...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    return (
      <div className="quiz-modal-overlay">
        <div className="quiz-modal">
          <div className="quiz-modal-header">
            <h2>Quiz Completed</h2>
            <button className="close-btn" onClick={handleClose}>
              <Close />
            </button>
          </div>
          <div className="quiz-modal-content">{renderResults()}</div>
          <div className="quiz-modal-footer">
            <button className="btn btn-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (shuffledQuestions.length === 0) {
    return (
      <div className="quiz-modal-overlay">
        <div className="quiz-modal">
          <div className="quiz-modal-header">
            <h2>Quiz</h2>
            <button className="close-btn" onClick={handleClose}>
              <Close />
            </button>
          </div>
          <div className="quiz-modal-content">
            <div className="quiz-error">
              <p>No questions available for this quiz.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = shuffledQuestions[currentQuestionIndex];

  return (
    <div className="quiz-modal-overlay">
      <div className="quiz-modal">
        <div className="quiz-modal-header">
          <h2>Video Quiz</h2>
          <button className="close-btn" onClick={handleClose}>
            <Close />
          </button>
        </div>

        <div className="quiz-modal-content">
          {timeRemaining !== null && (
            <div className="quiz-timer">
              <Timer className="timer-icon" />
              <span className="timer-text">
                Time remaining: {formatTime(timeRemaining)}
              </span>
            </div>
          )}

          <div className="quiz-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${
                    ((currentQuestionIndex + 1) / shuffledQuestions.length) *
                    100
                  }%`,
                }}
              />
            </div>
            <span className="progress-text">
              Question {currentQuestionIndex + 1} of {shuffledQuestions.length}
            </span>
          </div>

          <div className="quiz-content">{renderQuestion(currentQuestion)}</div>

          <div className="quiz-navigation">
            <div className="nav-buttons">
              <button
                className="nav-btn prev-btn"
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </button>

              {currentQuestionIndex < shuffledQuestions.length - 1 ? (
                <button
                  className="nav-btn next-btn"
                  onClick={handleNextQuestion}
                >
                  Next
                </button>
              ) : (
                <button
                  className="nav-btn submit-btn"
                  onClick={handleSubmitQuiz}
                  disabled={submitting}
                >
                  {submitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="btn-icon" />
                      Submit Quiz
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="question-navigation">
              {shuffledQuestions.map((_, index) => (
                <button
                  key={index}
                  className={`question-nav-btn ${
                    index === currentQuestionIndex ? "active" : ""
                  } ${answers[shuffledQuestions[index]?.id] ? "answered" : ""}`}
                  onClick={() => handleJumpToQuestion(index)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
